// src/hooks/useStudioResource.ts
import { useCallback, useEffect, useRef, useState } from 'react';
import { toDisplayMessage } from '../services/studioApi';

interface StudioResource<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  /** Re-runs the loader. Wired to the retry button in StudioErrorState. */
  reload: () => void;
  setData: (value: T) => void;
}

/**
 * Loads one async resource with proper loading/error state, aborting the in-flight
 * request on unmount or when the dependencies change.
 *
 * This is what the rest of the app is missing: the category pages swallow errors into
 * `window.location.reload()` and leak requests when filters change mid-flight.
 */
export function useStudioResource<T>(
  loader: (signal: AbortSignal) => Promise<T>,
  deps: readonly unknown[],
): StudioResource<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  const loaderRef = useRef(loader);
  loaderRef.current = loader;

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    setLoading(true);
    setError(null);

    loaderRef
      .current(controller.signal)
      .then((result) => {
        if (!active) return;
        setData(result);
      })
      .catch((caught: unknown) => {
        if (!active || controller.signal.aborted) return;
        setError(toDisplayMessage(caught, 'We could not load this right now.'));
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, attempt]);

  const reload = useCallback(() => setAttempt((value) => value + 1), []);

  return { data, loading, error, reload, setData };
}

export default useStudioResource;
